import React, { useEffect } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  SvgIcon,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAppSelector, useAppDispatch } from "../../../../Redux/Store/Hooks";
import { clearListuser } from "../../../../Redux/Slices/User/ListUserSlice";
import { clearLoginStatus } from "../../../../Redux/Slices/LoginSlicer/LoginSlicer";
import { clearDashboardChart } from "../../../../Redux/Slices/DashboardChart/DashboardChart";
import logo from "../../../../Assets/Images/logomobile.svg";
import "./SideNavbar.scss";
import { IMenuItem } from "../../../../Components/Sidnavbar/ISidnavbar";
import { StudyMaterialCartCount } from "../../../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialCartCount";
import { ReactComponent as Dashboard } from "../../../../Assets/Images/Dashboard.svg";
import { ReactComponent as BookletIcon } from "../../assets/images/study-material.svg";
import { ReactComponent as CartIcon } from "../../assets/images/cart.svg";
import { ReactComponent as NoteIcon } from "../../assets/images/lesson-plan.svg";
import { ReactComponent as Chat } from "../../../../Assets/Images/chat with eliot.svg";
import { ReactComponent as Support } from "../../../../Assets/Images/support.svg";
import { ReactComponent as Video } from "../../../../Assets/Images/Videos.svg";
import { ReactComponent as CalendarIcon } from "../../../../Assets/Images/calender.svg";
import { decode } from "../../../../Utils/encodeDecode";
import { Layout as LayoutIcon, BookOpen as BookIcon, Award as AwardIcon, Calendar1Icon } from "lucide-react";

const getDynamicIcon = (iconName: string | any) => {
  if (typeof iconName !== "string") return iconName;

  const iconMap: { [key: string]: React.ReactNode } = {
    Dashboard: <SvgIcon component={Dashboard} inheritViewBox />,
    Booklet: <SvgIcon component={BookletIcon} inheritViewBox />,
    Cart: <SvgIcon component={CartIcon} inheritViewBox />,
    Note: <SvgIcon component={NoteIcon} inheritViewBox />,
    Chat: <SvgIcon component={Chat} inheritViewBox />,
    Support: <SvgIcon component={Support} inheritViewBox />,
    Videopage: <SvgIcon component={Video} inheritViewBox />,
    chat: <SvgIcon component={Chat} inheritViewBox />,
    support: <SvgIcon component={Support} inheritViewBox />,
    videopage: <SvgIcon component={Video} inheritViewBox />,
    Videos: <SvgIcon component={Video} inheritViewBox />,
    videos: <SvgIcon component={Video} inheritViewBox />,
    Schedule: <Calendar1Icon size={22} />,
    TutoringDashboard: <LayoutIcon size={22} />,
    TutoringCourses: <BookIcon size={22} />,
    TutoringGrades: <AwardIcon size={22} />,
  };

  return iconMap[iconName] || <SvgIcon component={Dashboard} inheritViewBox />;
};

function hasChildren(item: IMenuItem): boolean {
  return !!(item.items && Array.isArray(item.items) && item.items.length > 0);
}

function normalizePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

function isPathActive(currentPath: string, itemPath: string): boolean {
  const normalizedCurrent = normalizePath(currentPath);
  const normalizedItem = normalizePath(itemPath);

  if (normalizedCurrent === normalizedItem) return true;

  if (normalizedItem.includes("study_materials")) {
    if (normalizedCurrent.includes("/cart")) {
      return normalizedItem.includes("/cart");
    }
    if (normalizedCurrent.startsWith(normalizedItem)) return true;
  }

  return false;
}

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: menu } = useAppSelector((state) => state.SideMenuPermission);
  useEffect(() => {
    dispatch(StudyMaterialCartCount());
  }, [dispatch]);

  const handleLogout = () => {
    sessionStorage.clear();
    dispatch(clearListuser());
    dispatch(clearLoginStatus());
    dispatch(clearDashboardChart());
    navigate("/login");
  };

  const visibleMenuItems = React.useMemo(() => {
    if (!menu?.Dashboard) return [];

    // The backend now controls which menus appear for ONLINE_TUTOR and other roles.
    // We simply filter based on view permissions provided by the API.
    const items = menu.Dashboard.filter((item) => item?.permissions?.view !== false);

    return items;
  }, [menu]);

  return (
    <Box className="sidebar">
      {/* Logo */}
      <Box className="sidebar-logo">
        <img src={logo} alt="Logo" />
      </Box>

      {/* Menu */}
      <List className="sidebar-menu">
        {visibleMenuItems.map((item, index) => (
          <MenuItemComponent
            key={item.to || `menu-${index}`}
            item={item}
            navigate={navigate}
            location={location}
          />
        ))}
      </List>
    </Box>
  );
};

export default Sidebar;

/* ---------------- Menu Item Components ---------------- */

type MenuProps = {
  item: IMenuItem;
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  isChild?: boolean;
};

const MenuItemComponent: React.FC<MenuProps> = ({
  item,
  navigate,
  location,
  isChild = false,
}) => {
  const resolvedItem = {
    ...item,
    icon: getDynamicIcon(item.icon),
  };
  const Component = hasChildren(resolvedItem) ? MultiLevel : SingleLevel;
  return (
    <Component
      item={resolvedItem}
      navigate={navigate}
      location={location}
      isChild={isChild}
    />
  );
};

const SingleLevel: React.FC<MenuProps> = ({
  item,
  navigate,
  location,
  isChild,
}) => {
  const currentPath =
    location.pathname === "/" ? "/tutor_dashboard" : location.pathname;
  const itemPath = normalizePath(item.to ?? "");
  const selected = isPathActive(currentPath, itemPath);
  const cartState = useAppSelector(
    (state) => state.StudyMaterialCartCountSlice
  );
  const CartCount = cartState?.data?.data ?? 0;
  console.log(CartCount, "CartCount");

  return (
    <ListItemButton
      selected={selected}
      onClick={() => navigate(itemPath)}
      className={`sidebar-item ${selected ? "active" : ""}`}
      sx={{
        pl: isChild ? 4 : 2,
        "&.Mui-selected": {
          backgroundColor: "rgba(0, 157, 255, 0.08)",
          "&:hover": { backgroundColor: "rgba(0, 157, 255, 0.12)" },
        },
      }}
    >
      <ListItemIcon
        sx={{
          color: selected ? "#009DFF !important" : "#000 !important",
          minWidth: "40px",
          "& svg": {
            fill: "none !important",
            color: selected ? "#009DFF !important" : "#000 !important",
          },
        }}
      >
        {item.title === "Cart" ? (
          <Badge
            badgeContent={CartCount}
            overlap="rectangular"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "12px",
                backgroundColor: "#ef4444",
                color: "#ffff",
                minWidth: 15,
                height: 15,
                top: -4,
                right: -4,
              },
            }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>

      <ListItemText
        primary={item.title}
        primaryTypographyProps={{
          sx: {
            fontSize: "16px",
            fontWeight: selected ? 600 : 400,
            color: selected ? "#009DFF" : "#000",
          },
        }}
      />
    </ListItemButton>
  );
};

const MultiLevel: React.FC<MenuProps> = ({
  item,
  navigate,
  location,
  isChild,
}) => {
  const { items: children } = item;
  const isParentActive = React.useMemo(() => {
    if (!children?.length) return false;
    return children.some((child) =>
      isPathActive(location.pathname, normalizePath(child.to ?? ""))
    );
  }, [children, location.pathname]);

  const [open, setOpen] = React.useState(isParentActive);

  React.useEffect(() => {
    setOpen(isParentActive);
  }, [isParentActive]);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        className={`sidebar-item ${isParentActive ? "active" : ""}`}
        sx={{
          pl: isChild ? 4 : 2,
          "&.active": { backgroundColor: "rgba(0, 157, 255, 0.08)" },
        }}
      >
        <ListItemIcon
          sx={{ color: isParentActive ? "#009DFF" : "#000", minWidth: "40px" }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            sx: {
              fontSize: "16px",
              fontWeight: isParentActive ? 600 : 400,
              color: isParentActive ? "#009DFF" : "#000",
            },
          }}
        />
        {open ? (
          <ExpandLessIcon sx={{ color: isParentActive ? "#009DFF" : "#000" }} />
        ) : (
          <ExpandMoreIcon sx={{ color: isParentActive ? "#009DFF" : "#000" }} />
        )}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children
            ?.filter((child) => child?.permissions?.view !== false)
            .map((child, idx) => (
              <MenuItemComponent
                key={child.to || `child-${idx}`}
                item={child}
                navigate={navigate}
                location={location}
                isChild
              />
            ))}
        </List>
      </Collapse>
    </>
  );
};
